/**
 * 🚀 Feature Flags Configuration
 * 
 * ⭐ แสดงทุก Feature เป็น Default
 * ⭐ ซ่อนเฉพาะที่กำหนดใน VITE_HIDE_FEATURES
 * 
 * วิธีใช้:
 * - ใน .env.production เพิ่ม: VITE_HIDE_FEATURES=games,marketing,dev-tools
 * - คั่นด้วย comma (,) ไม่ต้องมี space
 * 
 * ตัวอย่าง:
 * VITE_HIDE_FEATURES=games,marketing,affiliates,reports,acl,cron-jobs,changelog
 */

// ============================================
// Environment Check
// ============================================

export const IS_DEV_MODE = 
  import.meta.env.VITE_DEV_MODE === 'true' || 
  import.meta.env.DEV === true;

// ============================================
// Hidden Features from ENV
// ============================================

/**
 * รายการ features ที่ต้องการซ่อน (จาก env variable)
 * Format: VITE_HIDE_FEATURES=games,marketing,affiliates
 */
const HIDDEN_FEATURES: string[] = (import.meta.env.VITE_HIDE_FEATURES || '')
  .split(',')
  .map((f: string) => f.trim().toLowerCase())
  .filter((f: string) => f.length > 0);

// Log hidden features
if (HIDDEN_FEATURES.length > 0) {
  console.log('🚫 [Features] Hidden:', HIDDEN_FEATURES);
}

// ============================================
// Feature Status (for badges only)
// ============================================

export type FeatureStatus = 'ready' | 'beta' | 'dev' | 'hidden';

/**
 * Feature Status - ใช้แสดง badge เท่านั้น (DEV, BETA)
 * ไม่ได้ใช้ซ่อน feature แล้ว - ใช้ VITE_HIDE_FEATURES แทน
 * 
 * Status:
 * - 'ready' = พร้อมใช้งาน (ไม่แสดง badge) [DEFAULT]
 * - 'beta'  = ทดสอบ (แสดง badge BETA สีส้ม)
 * - 'dev'   = กำลังพัฒนา (แสดง badge DEV สีฟ้า)
 * 
 * 📌 หมายเหตุ:
 * - ถ้าไม่กำหนด = 'ready' (default)
 * - children จะ inherit status จาก parent อัตโนมัติ
 * - กำหนดเฉพาะที่ต่างจาก default หรือต่างจาก parent
 */
export const FEATURES: Record<string, FeatureStatus> = {
  // ═══════════════════════════════════════════════════════════════
  // 👑 กลุ่มสมาชิก (VIP, etc.)
  // ═══════════════════════════════════════════════════════════════
  'member-groups': 'dev',

  // ═══════════════════════════════════════════════════════════════
  // 🎮 เกม - parent ready, บาง children เป็น dev
  // ═══════════════════════════════════════════════════════════════
  'games/providers': 'dev',              // ค่ายเกม
  'games/categories': 'dev',             // หมวดหมู่
  'games/settings': 'dev',               // ตั้งค่าเกม
  'games/report': 'dev',                 // รายงานเกม

  // ═══════════════════════════════════════════════════════════════
  // 📢 การตลาด - parent dev (children inherit)
  // ═══════════════════════════════════════════════════════════════
  'marketing': 'dev',

  // ═══════════════════════════════════════════════════════════════
  // 🤝 ตัวแทน - parent dev (children inherit)
  // ═══════════════════════════════════════════════════════════════
  'affiliates': 'dev',

  // ═══════════════════════════════════════════════════════════════
  // 📈 รายงาน - parent ready, บาง children เป็น dev
  // ═══════════════════════════════════════════════════════════════
  'reports/financial': 'dev',            // การเงิน
  'reports/members': 'dev',              // สมาชิก
  'reports/transactions': 'dev',         // ธุรกรรม
  'reports/banks': 'dev',                // ธนาคาร

  // ═══════════════════════════════════════════════════════════════
  // ⚙️ ตั้งค่า - บางหมวดหมู่เป็น dev
  // ═══════════════════════════════════════════════════════════════
  'settings/members': 'dev',             // ตั้งค่าสมาชิก
  'settings/promotions': 'dev',          // ตั้งค่าโปรโมชั่น
  'settings/notifications': 'ready',     // ตั้งค่าแจ้งเตือน ✅ Telegram API พร้อมใช้งาน

  // ═══════════════════════════════════════════════════════════════
  // 🛠️ เครื่องมือพัฒนา (Dev Only)
  // ═══════════════════════════════════════════════════════════════
  'dev-tools': 'dev',
};

/**
 * ดึง Feature Status (inherit จาก parent ถ้าไม่ได้กำหนด)
 * 
 * ลำดับการตรวจสอบ:
 * 1. ดูใน FEATURES map ก่อน
 * 2. ถ้าไม่มี ดู parent (เช่น games/list -> games)
 * 3. ถ้าไม่มี return 'ready' (default)
 */
export function getFeatureStatus(featureKey: string): FeatureStatus {
  const key = featureKey.toLowerCase();
  
  // 1. ตรวจสอบ key โดยตรง
  if (FEATURES[key]) {
    return FEATURES[key];
  }
  
  // 2. ตรวจสอบ parent
  const parentKey = key.split('/')[0];
  if (parentKey !== key && FEATURES[parentKey]) {
    return FEATURES[parentKey];
  }
  
  // 3. Default
  return 'ready';
}

// ============================================
// Helper Functions
// ============================================

/**
 * ตรวจสอบว่า feature พร้อมแสดงหรือไม่
 * 
 * Logic:
 * 1. ถ้าอยู่ใน VITE_HIDE_FEATURES = ซ่อน
 * 2. ถ้า status = 'dev' และไม่ใช่ dev mode = ซ่อน
 * 3. ถ้า status = 'hidden' = ซ่อนเสมอ
 * 4. นอกนั้น = แสดง (ready, beta)
 * 
 * @param featureKey - ชื่อ feature เช่น 'members', 'transactions/pending'
 * @returns true ถ้าควรแสดง feature นี้
 */
export function isFeatureEnabled(featureKey: string): boolean {
  const key = featureKey.toLowerCase();
  
  // 1. ถ้าอยู่ใน HIDDEN_FEATURES (จาก env) = ซ่อน
  if (HIDDEN_FEATURES.includes(key)) {
    return false;
  }
  
  // 2. ตรวจสอบ parent path ด้วย (เช่น games/list ถ้าซ่อน games ก็ซ่อนด้วย)
  const parentKey = key.split('/')[0];
  if (parentKey !== key && HIDDEN_FEATURES.includes(parentKey)) {
    return false;
  }
  
  // 3. ดึง status (inherit จาก parent ถ้าไม่มี)
  const status = getFeatureStatus(key);
  
  // 4. ถ้า status = 'hidden' = ซ่อนเสมอ
  if (status === 'hidden') {
    return false;
  }
  
  // 5. ถ้า status = 'dev' และไม่ใช่ dev mode = ซ่อน
  if (status === 'dev' && !IS_DEV_MODE) {
    return false;
  }
  
  // 6. Default = แสดง (ready, beta)
  return true;
}

/**
 * แปลง path เป็น feature key
 * เช่น '/transactions/pending' => 'transactions/pending'
 */
export function pathToFeatureKey(path: string): string {
  return path.replace(/^\//, '').replace(/\/$/, '');
}

/**
 * ตรวจสอบว่า route path พร้อมใช้งานหรือไม่
 */
export function isRouteEnabled(path: string): boolean {
  const key = pathToFeatureKey(path);
  return isFeatureEnabled(key);
}

/**
 * Filter nav items ตาม feature flags
 */
export function filterNavItems<T extends { href: string; children?: T[] }>(items: T[]): T[] {
  return items
    .filter(item => {
      const key = pathToFeatureKey(item.href);
      return isFeatureEnabled(key);
    })
    .map(item => ({
      ...item,
      children: item.children ? filterNavItems(item.children) : undefined,
    }))
    // ลบ parent ที่ไม่มี children เหลือ (ถ้าเดิมมี children)
    .filter(item => {
      if (item.children && item.children.length === 0) {
        // ถ้า parent มี children แต่ถูก filter หมด = ซ่อน parent ด้วย
        return false;
      }
      return true;
    });
}

/**
 * Get feature status badge color
 */
export function getFeatureStatusColor(status: FeatureStatus): string {
  switch (status) {
    case 'ready': return 'bg-emerald-500';
    case 'beta': return 'bg-amber-500';
    case 'dev': return 'bg-blue-500';
    case 'hidden': return 'bg-gray-500';
    default: return 'bg-gray-500';
  }
}

/**
 * Get feature status label
 */
export function getFeatureStatusLabel(status: FeatureStatus): string {
  switch (status) {
    case 'ready': return 'พร้อมใช้งาน';
    case 'beta': return 'ทดสอบ';
    case 'dev': return 'กำลังพัฒนา';
    case 'hidden': return 'ซ่อน';
    default: return status;
  }
}

// ============================================
// Debug (เฉพาะ dev mode)
// ============================================

if (IS_DEV_MODE) {
  console.log('🚀 [Features] Dev Mode:', IS_DEV_MODE);
  console.log('🚀 [Features] Non-ready features:', 
    Object.entries(FEATURES)
      .filter(([_, status]) => status !== 'ready')
      .map(([key, status]) => `${key} (${status})`)
  );
}

export default {
  IS_DEV_MODE,
  FEATURES,
  getFeatureStatus,
  isFeatureEnabled,
  isRouteEnabled,
  filterNavItems,
};

